import { React, useEffect, useState, useRef } from 'react'
import { Box, Card, Divider, Icon, Typography, useMediaQuery } from '@mui/material'
import Bugsnag from '@bugsnag/js'
import { useTheme } from '@mui/material'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import { Formik, Form, ErrorMessage, FieldArray } from 'formik'
import * as Yup from 'yup'
import { useMutation, useQueryClient } from 'react-query'
import { handleValidationErrors, handleScroll } from 'services/helper/handleValidationErrors'

// API
import { postTaxData } from 'services/api/modules/staff-management/staffManagementDashboard'
import AdxDatePicker from 'components/custom/AdxDatePicker'
import { format } from 'date-fns'
import AdxButton from 'components/custom/AdxButton'

const marriageStatusDropdown = [
  {
    id: 1,
    name: 'Single',
  },
  {
    id: 2,
    name: 'Married But Spouse Not Working',
  },
  {
    id: 3,
    name: 'Married But Spouse Working',
  },
  {
    id: 4,
    name: 'Divorced/Widow/Widower',
  },
]

const educationDropdown = [
  {
    id: 0,
    education: 'No Education',
  },
  {
    id: 1,
    education: 'High school & below',
  },
  {
    id: 2,
    education: 'Diploma (Local) or Higher',
  },
  {
    id: 3,
    education: 'Degree (Overseas) or Higher',
  },
]

const HouseholdDetails = ({ data, employeeID, windowCheckEmployeeFile }) => {
  const queryClient = useQueryClient()
  const formikRef = useRef()

  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.up('sm'))
  const [disable, setDisable] = useState(true)

  const [initialValues, setInitialValues] = useState({
    marriage_status: '',
    spouse_disability_status: '',
    children: [],
  })

  useEffect(() => {
    if (data) {
      setInitialValues({
        marriage_status: data?.data?.marriage_status,
        spouse_disability_status: data?.data?.spouse_disability_status,
        children: data?.data?.children
          ? data?.data?.children.map((item) => {
              return {
                id: item.id,
                dob: new Date(item.dob),
                education_status: item.education_status,
                disability_status: item.disability_status,
              }
            })
          : [],
      })
    }
  }, [data])

  const validationSchema = Yup.object().shape({
    marriage_status: Yup.number().nullable().required('Marriage Status is required'),
    spouse_disability_status: Yup.number().nullable().required('Disability Status is required'),
    children: Yup.array().of(
      Yup.object().shape({
        dob: Yup.string().nullable().required('Date of birth is required'),
        education_status: Yup.number().nullable().required('Education Status is required'),
        disability_status: Yup.number().nullable().required('Disability Status is required'),
      })
    ),
  })

  const cancelEditForm = (resetForm) => {
    resetForm()
    setDisable(true)
  }

  const mutation = useMutation((value) => postTaxData(employeeID, value), {
    onSuccess: (response) => {
      queryClient.invalidateQueries(['detailedEmployeeData', employeeID])
      queryClient.invalidateQueries(['employeeTaxData', employeeID])
      setDisable(true)
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const handleSubmitButton = async (values, action) => {
    const { ...rest } = values
    let newArray = values.children.map((item) => {
      const { dob, ...rest } = item
      return {
        ...rest,
        dob: format(dob, 'MM/dd/yyyy'),
      }
    })
    let newValues = { ...rest, children: newArray }
    mutation.mutate(newValues)
  }

  const handleCheckRef = async (handleFormikSubmit) => {
    await handleFormikSubmit()
    const { errors = {} } = formikRef.current
    handleScroll(errors)
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        innerRef={formikRef}
        onSubmit={handleSubmitButton}
      >
        {({
          values,
          touched,
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          resetForm,
        }) => (
          <Form>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                alignItems: 'center',
                borderColor: 'gray-line.main',
                p: 2,
              }}
            >
              <Typography color="light-gray-text.main" fontWeight={500}>
                Household
              </Typography>
            </Box>
            <Box
              p={1.75}
              borderBottom={windowCheckEmployeeFile && '1px solid'}
              sx={{
                borderColor: 'gray-line.main',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box>
                <AdxDropdown
                  label="Marriage Status"
                  disabled={disable}
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Marriage Status"
                  name="marriage_status"
                  value={values.marriage_status}
                  onChange={(event) => {
                    if (event.target.value === 1 || event.target.value === 4) {
                      if (values.marriage_status !== 1 || values.marriage_status !== 4) {
                        setFieldValue('spouse_disability_status', 0)
                      }
                    } else {
                      if (values.marriage_status === 1 || values.marriage_status === 4) {
                        setFieldValue('spouse_disability_status', null)
                      }
                    }
                    handleChange(event)
                  }}
                  sx={{ width: 1 }}
                  errorMessage={<ErrorMessage name="marriage_status" component={AdxErrorMessage} />}
                  isErrorShown={touched.marriage_status && errors.marriage_status}
                >
                  {marriageStatusDropdown.map((status) => (
                    <AdxMenuItem value={status.id} selectedvalue={values.marriage_status}>
                      {status.name}
                    </AdxMenuItem>
                  ))}
                </AdxDropdown>
              </Box>
              <Box>
                <AdxDropdown
                  label="Spouse Disability Status"
                  disabled={
                    disable
                      ? true
                      : values.marriage_status === 1 || values.marriage_status === 4
                      ? true
                      : false
                  }
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Spouse Disability Status"
                  value={values.spouse_disability_status}
                  name="spouse_disability_status"
                  onChange={handleChange}
                  sx={{ width: 1 }}
                  errorMessage={
                    <ErrorMessage name="spouse_disability_status" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.spouse_disability_status && errors.spouse_disability_status}
                >
                  <AdxMenuItem value={1} selectedvalue={values.spouse_disability_status}>
                    Yes
                  </AdxMenuItem>
                  <AdxMenuItem value={0} selectedvalue={values.spouse_disability_status}>
                    No
                  </AdxMenuItem>
                </AdxDropdown>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <AdxInput
                  label="Number of Children"
                  disabled
                  sxLabel={{
                    color: disable ? 'light-gray-text.main' : 'dark-text.main',
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Number of Children"
                  value={values.children.length}
                  sx={{ width: 1 }}
                />
              </Box>
              <Box>
                <Card variant="outlined" sx={{ width: '100' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '100%',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: 'center',
                      gap: 1,
                      p: 2,
                    }}
                  >
                    {matches ? <Box sx={{ width: 1 / 4 }}></Box> : null}
                    <Box
                      sx={{
                        display: 'flex',
                        width: { xs: 1, sm: 3 / 4 },
                        alignItems: 'center',
                        gap: 2.5,
                      }}
                    >
                      <Box sx={{ width: { xs: 1 / 3, sm: 1.5 / 5 } }}>
                        <Typography align="center" variant="body1" color="dark-gray-text.main">
                          Date of birth
                        </Typography>
                      </Box>
                      <Box sx={{ width: { xs: 1 / 3, sm: 2 / 5 } }}>
                        <Typography align="center" variant="body1" color="dark-gray-text.main">
                          Education status
                        </Typography>
                      </Box>
                      <Box sx={{ width: { xs: 1 / 3, sm: 1.5 / 5 } }}>
                        <Typography align="center" variant="body1" color="dark-gray-text.main">
                          Disability status
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider light />
                  <FieldArray
                    name="children"
                    render={(arrayHelpers) => {
                      return (
                        <>
                          {values.children.map((item, index) => {
                            return (
                              <Box
                                key={index}
                                sx={{
                                  display: 'flex',
                                  width: '100%',
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  alignItems: 'center',
                                  p: 2,
                                }}
                              >
                                <Box sx={{ width: 1 / 4 }}>
                                  <Typography variant="body1" color="dark-gray-text.main">
                                    Child {index + 1}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    width: { xs: 1, sm: 3 / 4 },
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                  }}
                                >
                                  <Box sx={{ width: { xs: 1 / 3, sm: 1.5 / 5 } }}>
                                    <AdxDatePicker
                                      disabled={disable}
                                      onChange={(e) => setFieldValue(`children[${index}].dob`, e)}
                                      name={`children[${index}].dob`}
                                      value={values.children[index].dob}
                                      disableFuture
                                      errorMessage={
                                        <ErrorMessage
                                          name={`children[${index}].dob`}
                                          component={AdxErrorMessage}
                                        />
                                      }
                                      isErrorShown={
                                        touched.children &&
                                        errors.children &&
                                        touched.children[index]?.dob &&
                                        errors.children[index]?.dob
                                      }
                                    />
                                  </Box>
                                  <Box sx={{ width: { xs: 1 / 3, sm: 2 / 5 } }}>
                                    <AdxDropdown
                                      name={`children[${index}].education_status`}
                                      disabled={disable}
                                      sxLabel={{
                                        color: disable ? 'light-gray-text.main' : 'dark-text.main',
                                      }}
                                      sxInput={{
                                        color: 'light-gray-text.main',
                                        backgroundColor: 'white.main',
                                      }}
                                      size="standard"
                                      value={values.children[index].education_status}
                                      onChange={handleChange}
                                      sx={{ width: { xs: '100%', sm: '100%' } }}
                                      errorMessage={
                                        <ErrorMessage
                                          name={`children[${index}].education_status`}
                                          component={AdxErrorMessage}
                                        />
                                      }
                                      isErrorShown={
                                        touched.children &&
                                        errors.children &&
                                        touched.children[index]?.education_status &&
                                        errors.children[index]?.education_status
                                      }
                                    >
                                      {educationDropdown.map((dropdown) => (
                                        <AdxMenuItem
                                          value={dropdown.id}
                                          selectedvalue={values.children.education_status}
                                        >
                                          {dropdown.education}
                                        </AdxMenuItem>
                                      ))}
                                    </AdxDropdown>
                                  </Box>
                                  <Box sx={{ width: { xs: 1 / 3, sm: 1 / 5 } }}>
                                    <AdxDropdown
                                      name={`children[${index}].disability_status`}
                                      disabled={disable}
                                      sxLabel={{
                                        color: disable ? 'light-gray-text.main' : 'dark-text.main',
                                      }}
                                      sxInput={{
                                        color: 'light-gray-text.main',
                                        backgroundColor: 'white.main',
                                      }}
                                      size="standard"
                                      value={values.children[index].disability_status}
                                      onChange={handleChange}
                                      sx={{ width: { xs: '100%', sm: '100%' } }}
                                      errorMessage={
                                        <ErrorMessage
                                          name={`children[${index}].disability_status`}
                                          component={AdxErrorMessage}
                                        />
                                      }
                                      isErrorShown={
                                        touched.children &&
                                        errors.children &&
                                        touched.children[index]?.disability_status &&
                                        errors.children[index]?.disability_status
                                      }
                                    >
                                      <AdxMenuItem
                                        value={1}
                                        selectedvalue={values.children.disability_status}
                                      >
                                        Yes
                                      </AdxMenuItem>
                                      <AdxMenuItem
                                        value={0}
                                        selectedvalue={values.children.disability_status}
                                      >
                                        No
                                      </AdxMenuItem>
                                    </AdxDropdown>
                                  </Box>
                                </Box>
                                {!disable && (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      arrayHelpers.remove(index)
                                    }}
                                  >
                                    <RemoveCircleIcon
                                      sx={{
                                        fontSize: 20,
                                        marginRight: -1,
                                      }}
                                      color="error"
                                    />
                                  </Box>
                                )}
                              </Box>
                            )
                          })}

                          <Box
                            sx={{
                              width: '100%',
                              p: 0.5,
                              borderTop: 1,
                              borderColor: 'gray-line.main',
                            }}
                          >
                            <AdxButton
                              variant={'ghost'}
                              disabled={disable}
                              fullWidth
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                              }}
                              onClick={() => {
                                arrayHelpers.push({
                                  dob: null,
                                  education_status: null,
                                  disability_status: null,
                                })
                              }}
                            >
                              <Icon
                                sx={{
                                  fontSize: 24,
                                  color: !disable ? 'alternate.main' : 'gray-background.main',
                                }}
                              >
                                add
                              </Icon>
                              <Typography variant="body1">Add Children</Typography>
                            </AdxButton>
                          </Box>
                        </>
                      )
                    }}
                  />
                </Card>
              </Box>
              {windowCheckEmployeeFile && !disable && (
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AdxButton
                    variant={'outlined'}
                    onClick={() => cancelEditForm(resetForm)}
                    sx={{ width: 0.2 }}
                  >
                    Cancel
                  </AdxButton>
                  <AdxButton onClick={() => handleCheckRef(handleSubmit)} sx={{ width: 0.2 }}>
                    Save
                  </AdxButton>
                </Box>
              )}
            </Box>
            {windowCheckEmployeeFile && disable && (
              <Box sx={{ p: 0.5 }}>
                <AdxButton
                  variant={'ghost'}
                  sx={{
                    pl: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                  }}
                  onClick={() => setDisable(false)}
                >
                  <Icon sx={{ fontSize: '15px', color: 'alternate.main' }}>edit</Icon>
                  <Typography fontWeight={500} color="alternate.main" ml={1.25}>
                    Edit household details
                  </Typography>
                </AdxButton>
              </Box>
            )}
          </Form>
        )}
      </Formik>
    </>
  )
}

export default HouseholdDetails
